import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewProductListingPageFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const tag = previewConfig.previewPersonalizationTag

    return {
      ...entries,
      items: entries.items.map((item) => {
        if (!item?.fields) return item

        const updatedFields = processNestedFieldsByTag(item.fields as Record<string, unknown>, tag)

        return { ...item, fields: updatedFields as typeof item.fields }
      }),
    }
  }

  const extractPanelsFromField = (fieldName: string, fieldValue: unknown): LocalePageEntry[] => {
    const panels: LocalePageEntry[] = []

    // Handle array fields
    if (Array.isArray(fieldValue)) {
      fieldValue.forEach((item) => {
        // Add the item itself if it has sys.id
        if (item?.sys?.id) {
          panels.push(item)
        }

        // Recursively extract from nested fields
        if (item?.fields && typeof item.fields === 'object') {
          Object.entries(item.fields).forEach(([nestedFieldName, nestedFieldValue]) => {
            panels.push(...extractPanelsFromField(nestedFieldName, nestedFieldValue))
          })
        }
      })
      return panels
    }

    // Handle single object with sys.id
    if (fieldValue && typeof fieldValue === 'object' && 'sys' in fieldValue) {
      const obj = fieldValue as Record<string, unknown>
      const sys = obj.sys as Record<string, unknown> | undefined
      if (sys?.id) {
        panels.push(fieldValue as LocalePageEntry)

        // Recursively extract from its nested fields
        if ('fields' in obj && obj.fields && typeof obj.fields === 'object') {
          Object.entries(obj.fields as Record<string, unknown>).forEach(([nestedFieldName, nestedFieldValue]) => {
            panels.push(...extractPanelsFromField(nestedFieldName, nestedFieldValue))
          })
        }
      }
    }

    return panels
  }

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return []

    const panels: PanelInfo[] = []
    const seenIds = new Set<string>()

    // Dynamically process all fields
    Object.entries(firstPage).forEach(([fieldName, fieldValue]) => {
      const extractedPanels = extractPanelsFromField(fieldName, fieldValue)

      extractedPanels.forEach((panel) => {
        if (panel?.sys?.id && !seenIds.has(panel.sys.id)) {
          seenIds.add(panel.sys.id)
          panels.push(getPanelInfo(panel))
        }
      })
    })

    return panels
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
  ) => {
    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return entries

    const processFieldValue = (fieldValue: unknown): unknown => {
      // Handle array fields
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .map((item) => {
            // Check if item should be shown
            const itemId = item?.sys?.id
            if (itemId && panelDisplayInfo[itemId]?.show === false) {
              return null // Mark for removal
            }

            // Recursively process nested fields
            if (item?.fields && typeof item.fields === 'object') {
              const processedFields = { ...item.fields }
              Object.entries(processedFields).forEach(([fieldName, nestedFieldValue]) => {
                const processed = processFieldValue(nestedFieldValue)
                if (processed === null) {
                  delete processedFields[fieldName]
                } else {
                  processedFields[fieldName] = processed
                }
              })

              return {
                ...item,
                fields: processedFields,
              }
            }

            return item
          })
          .filter(Boolean) // Remove nulls
      }

      // Handle single object with sys.id
      if (fieldValue && typeof fieldValue === 'object' && 'sys' in fieldValue) {
        const obj = fieldValue as Record<string, unknown>
        const sys = obj.sys as Record<string, unknown> | undefined
        const itemId = sys?.id as string | undefined

        // Remove if should not be shown
        if (itemId && panelDisplayInfo[itemId]?.show === false) {
          return null
        }

        // Recursively process nested fields
        if ('fields' in obj && obj.fields && typeof obj.fields === 'object') {
          const processedFields = { ...(obj.fields as Record<string, unknown>) }
          Object.entries(processedFields).forEach(([fieldName, nestedFieldValue]) => {
            const processed = processFieldValue(nestedFieldValue)
            if (processed === null) {
              delete processedFields[fieldName]
            } else {
              processedFields[fieldName] = processed
            }
          })

          return {
            ...obj,
            fields: processedFields,
          }
        }

        return fieldValue
      }

      // Return as-is for non-panel fields
      return fieldValue
    }

    // Process all top-level fields
    Object.entries(firstPage).forEach(([key, value]) => {
      const processed = processFieldValue(value)
      if (processed === null) {
        delete firstPage[key]
      } else {
        firstPage[key] = processed
      }
    })

    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
